import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Rect, Text, Image, Shape, Line, Circle, Ellipse} from 'react-konva';
import Konva from 'konva';
import useImage from 'use-image';
import { useAPI } from '../services/API';
import { AiOutlineColumnWidth, AiOutlineColumnHeight } from 'react-icons/ai';
import {Typography, Box, Button, ButtonGroup, Modal, Chip, CircularProgress } from '@mui/material';
import dayjs from "dayjs";
import 'dayjs/locale/fr';
import { prenomNOM } from '../services/Utils';
import { useTheme } from '@mui/material/styles';
import { useNavigateWithCommune } from '../services/Utils';


const componentMap = {
    Rect: Rect,
    Line: Line,
    Text: Text,
    Shape: DynamicShape,
    Image : CustomImage,
    Ellipse : Ellipse,
};

function DynamicShape({ shapeData, ...props }) {
  return (
    <Shape
      {...props}
      sceneFunc={(context, shape) => {
        for (let { command, args } of shapeData) {
          if (typeof context[command] === 'function') {
            context[command](...args);
          }
        }
        context.fillStrokeShape(shape); // obligatoire avec Konva
      }}
    />
  );
}

function CustomImage({src, ...props}){
    const [image] = useImage(src);
    return(
        <Image image={image} {...props}/>
    )
}

export default function Plan({}){
    const [fleche] = useImage('/data/fleche_arriere.png');
    const [currentStage, setCurrentStage] = useState("general");
    const [fit, setFit] = useState("");
    const [donneesPlan, setDonneesPlan] = useState([]);
    const [emplacements, setEmplacements] = useState([]);
    const ref = useRef(null)
    const [popupOpen, setPopupOpen] = useState(false)
    const [emplacementSelectionne, setEmplacementSelectionne] = useState(undefined);

    const navigate = useNavigateWithCommune();
    const api = useAPI();

    useEffect(() => {
        Promise.all([
            api("GET", "DonneesPlan"),
            api("GET", "Emplacement", {}, { occupation: true })
        ]).then(([dpRes, empRes]) => {
            if (Array.isArray(dpRes.json)) setDonneesPlan(dpRes.json);
            if (Array.isArray(empRes.json)) setEmplacements(empRes.json);
            setFit("widthFit");
        });
    }, []);

    const currentStageData = useMemo(
        () => donneesPlan.find(s => s.designation === currentStage),
        [donneesPlan, currentStage]
    );

    const { stageScale, x } = useMemo(() => {
        if (!currentStageData) return { stageScale: 1, x: 0 };
        const l = currentStageData.largeur;
        const h = currentStageData.hauteur;
        if (fit === "widthFit") return { stageScale: 800 / l, x: 0 };
        if (fit === "heightFit") {
            const scale = (window.innerHeight - 60) / h;
            return { stageScale: scale, x: (800 - l * scale) / (2 * scale) };
        }
        return { stageScale: 1, x: 0 };
    }, [currentStageData, fit]);

    const handleClick = (emplacement)=>{
        setEmplacementSelectionne(emplacements.find(e=>e.nom === emplacement))
        setPopupOpen(true)
    }

    const callbackPopup = (action)=>{
        if(action == false){
            setPopupOpen(false)
        }
    }

    return (
        <>
            <div style={{position:"relative"}}>
                <Box sx={{position:"absolute", left: "calc(50% - 210px)", translate: "-50%"}}>
                    <Chip label="Tombe occupée" sx={{bgcolor:"emeraude.light"}}/>
                    <Chip label="Tombe disponible" sx={{bgcolor:"emeraude.main"}}/>
                </Box>
                <Box sx={{position:"absolute", left: "calc(50% + 210px)", translate: "-50%"}}>
                    <Chip label="Cavurne occupée" sx={{bgcolor:"orchide.light"}}/>
                    <Chip label="Cavurne disponible" sx={{bgcolor:"orchide.main"}}/>
                </Box>
                <Typography variant="h5" sx={{textAlign:"center"}}>Plan du cimetière</Typography>
                <ButtonGroup variant="outlined" sx={{position:"absolute", top:0, right:1, "& .MuiButton-startIcon":{mx:0}}}>
                    <Button variant="outlined" startIcon={<AiOutlineColumnWidth />} onClick={()=>setFit("widthFit")}></Button>
                    <Button variant="outlined" startIcon={<AiOutlineColumnHeight />} onClick={()=>setFit("heightFit")}></Button>
                </ButtonGroup>
            </div>
            <div style={{width:820, margin:'auto', height:"calc(100vh - 60px)", overflow: "auto"}}  ref={ref}>
                <div>

                {donneesPlan.map(stage=>
                    <Stage key={stage.id} width={800} height={stageScale*(currentStageData?.hauteur||1)} scale={{x:stageScale,y:stageScale}} visible={currentStage == stage.designation}>
                        <Layer x={x}>
                            {JSON.parse(stage.contenu).map((item, index) => {
                                const Component = componentMap[item.type];
                                if (!Component) return null;

                                const props = { ...item.props };

                                // Event mapping
                                if (props.onClickKey) {
                                    if(props.onClickKey === "jardinSouvenirs"){
                                        props.onClick = () => navigate("/concession/jardinSouvenirs");
                                    }else{
                                        props.onClick = () => setCurrentStage(props.onClickKey);
                                        //delete props.onClickKey;
                                    }
                                }

                                return <Component key={index} {...props} />;
                            })}
                        </Layer>
                        <Layer x={x}>
                            {emplacements.filter(e=>e.stage_designation==stage.designation).map(t=>
                                <Tomb key={t.nom} {...t} onClick={handleClick}/> 
                            )}
                        </Layer>
                        {stage.designation !=="general" &&
                            <Layer x={x}>
                                <Image image={fleche} width={80} height={80} x={50} y={50} onClick={()=>setCurrentStage("general")}/>
                            </Layer>
                        }
                    </Stage>
                )}
                </div>
            </div>

            <PopupEmplacement open={popupOpen} emplacement={emplacementSelectionne} callbackPopup={callbackPopup} />
        </>
    );
}

function PopupEmplacement({open, emplacement, callbackPopup}){
    const [innoccupe, setInnoccupe] = useState(false);
    const [concession, setConcession] = useState(undefined);
    const [defunts, setDefunts] = useState([]);
    const [concessionnaire, setConcessionnaire] = useState({});
    const [renouvellement, setRenouvellement] = useState({});
    const [inProgress, setInProgress] = useState(true);

    const navigate = useNavigateWithCommune();
    const api = useAPI();

    const stylePopup = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "40%",
        minWidth: "400px",
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        maxHeight : "95vh",
        overflow : "auto"
    };

    const isJardinSouvenir = emplacement?.nom == "JS";

    useEffect(() => {
        if(!open) return

        setInProgress(true)
        let promises=[]

        api("GET", "Concession", {}, {Emplacement_nom:emplacement.nom, enCours:1})
        .then(res=>{
            if(res.status===200){
                let concession = res.json[0]
                setConcession(concession)
                setInnoccupe(false)

                promises.push(
                    api("GET", "Defunt",{},{Concession_numero:concession.numero, OrderBy:"date_inhumation DESC"})
                    .then(res => setDefunts(res.json))
                )
                
                promises.push(
                    api("GET","Concessionnaire/actif",{},{Concession_numero:concession.numero})
                    .then(res=>setConcessionnaire(res.json))
                )
                
                promises.push(
                    api("GET","Renouvellement/dernier",{},{Concession_numero:concession.numero})
                    .then(res=>setRenouvellement(res.json))
                )
                Promise.all(promises).then(allRes=>{
                    setInProgress(false)
                })
            }else if(res.status===204){
                setInnoccupe(true)
                setInProgress(false)
            }
        })
    }, [open]);

    const callback = (action)=>{
        setConcession(undefined)
        setDefunts([])
        callbackPopup(action)
    }

    return(
        <Modal open={open||false} onClose={()=>callback(false)}>
            <Box sx={stylePopup}>
                {inProgress && <Box sx={{margin: 3, textAlign:"center"}}><CircularProgress/></Box>}
                {!inProgress && 
                    <Box sx={{m:4}}>
                        {isJardinSouvenir ?
                            <Typography variant="h4" sx={{textAlign:"center", mb:2}}>Jardin des souvenirs</Typography>
                        :
                            <Typography variant="h4" sx={{textAlign:"center", mb:2}}>Emplacement {emplacement?.nom}</Typography>
                        }
                        {innoccupe ?
                        <>
                            <Typography>Cet emplacement est inoccupé.<br/>Souhaitez-vous créer une nouvelle concession ici ?</Typography>
                            <Button onClick={()=>navigate("/concession/creer/"+emplacement.nom)} sx={{display:"block", mx:"auto", mt:2}} variant='contained'>Créer une concession</Button>
                        </>
                        :
                        <>
                            {!isJardinSouvenir && <>
                                <Typography variant="h5"><strong>Concession : </strong>{concession?.numero}</Typography>
                                <Typography variant="h5"><strong>Description : </strong>{concession?.description}</Typography>
                            </>}
                            <Typography variant="h5"><strong>Défunts : </strong></Typography>
                            <ul style={{marginTop:0}}>
                                {defunts.map(d=>{
                                    let annee_naissance = d?.date_naissance==="?" ? "inconnue" : dayjs(d?.date_naissance).year()
                                    let annee_inhumation = d?.date_inhumation==="?" ? "inconnue" : dayjs(d?.date_inhumation).year()
                                    return(
                                        <li key={d.id}><Typography variant="h6">{prenomNOM(d)}
                                            {/*d?.prenom + " "} 
                                            {d?.nom_usage? d?.nom_usage.toUpperCase() + " (né.e "+d?.nom_naissance?.toUpperCase() +")" : d?.nom_naissance?.toUpperCase()}
                                    {" ("+annee_naissance+"-"+annee_inhumation+")"*/}
                                        </Typography></li>
                                    )
                                })}
                            </ul>
                            {!isJardinSouvenir && <>
                                <Typography variant="h5"><strong>Concessionnaire : </strong>{concessionnaire.nom_naissance===undefined ? <span style={{color:"red"}}>Non défini</span> : prenomNOM(concessionnaire)}</Typography>
                                <Typography variant="h5"><strong>Type : </strong>{concession?.type}</Typography>
                                <Typography variant="h5"><strong>Expirant le : </strong>{renouvellement?.duree===null? <span style={{color:"red"}}>Renouvellement à contrôler</span> : (renouvellement?.duree==="perpétuel"?"- (perpétuel)" : dayjs(renouvellement?.expirant_le).format("DD-MM-YYYY"))}</Typography>
                            </>}
                            <Button onClick={()=>navigate("concession/"+concession?.numero)} sx={{display:"block", mx:"auto", mt:2}} variant='contained'>Détails</Button>
                        </>
                        }
                    </Box>
                }
            </Box>
      </Modal>
    )
}
  
const customComponents = {
    "Rect" : RectTomb,
    "Line" : LineTomb
}
  
function Tomb ({dessin_forme,...other}) {
    if(Object.keys(customComponents).includes(dessin_forme)){
        const CustomComponent = customComponents[dessin_forme]
        return <CustomComponent {...other}/> 
    }
}
  
function RectTomb ({nom, type, dessin_x, dessin_y, dessin_largeur, dessin_hauteur, dessin_texte_offset_x, dessin_texte_offset_y, occupe, onClick}){
    
    const theme = useTheme()
    return (
          <>
              <Rect
                  x={dessin_x}
                  y={dessin_y}
                  width={dessin_largeur}
                  height={dessin_hauteur}
                  stroke="black"
                  onClick={()=>onClick(nom)}
                  fill={type=="Cavurne"?(occupe?theme.palette.orchide.light:theme.palette.orchide.main):(occupe?theme.palette.emeraude.light:theme.palette.emeraude.main)}
              />
              <Text
                  text={nom} 
                  //fontSize={30}
                  fontSize={Math.min(30,dessin_largeur*1.5/nom.length)}
                  x={dessin_x + dessin_texte_offset_x}
                  y={dessin_y + dessin_texte_offset_y}
                  width={dessin_largeur}
                  height={dessin_hauteur}
                  align='center'
                  verticalAlign='middle'
                  onClick={()=>onClick(nom)}
              />
          </>
      )
  }
function LineTomb ({nom, type, dessin_x, dessin_y, dessin_largeur, dessin_hauteur, dessin_texte_offset_x, dessin_texte_offset_y, dessin_data, occupe, onClick}){

    const theme = useTheme()
    return (
          <>
              <Line
                  x={0}
                  y={0}
                  points={JSON.parse(dessin_data).points}
                  closed
                  onClick={()=>onClick(nom)}
                  fill={occupe?"#F0AEA888":"#aa291d88"}
              />
              <Text
                  text={nom} 
                  fontSize={30}
                  //fontSize={Math.min(30,dessin_largeur*1.5/nom.length)}
                  x={dessin_texte_offset_x-50}
                  y={dessin_texte_offset_y}
                  width={100}
                  //height={20}
                  align='center'
                  verticalAlign='middle'
                  onClick={()=>onClick(nom)}
              />
          </>
      )
  }

  
const ColoredRect = () => {
    const [color, setColor] = useState('green');
  
    const handleClick = () => {
      setColor(Konva.Util.getRandomColor());
    };
  
    return (
      <Rect
        x={20}
        y={20}
        width={50}
        height={50}
        fill={color}
        shadowBlur={5}
        onClick={handleClick}
      />
    );
  };

